/** @module api */

import { getApiUrl } from '@/api/configure'

/**
 * The [URL]{@link https://en.wikipedia.org/wiki/URL} endpoint for API requests. Specific path has to be postfixed.
 */
let API_URL

export function resetApiUrl() {
  API_URL = undefined
}

/**
 * API call (GET, POST, PATCH, PUT, DELETE)
 *
 * @function
 *
 * @param {object} arg
 * @param {string} arg.method has to be a HTTP method. Defaults to 'GET'.
 * @param {string} arg.url (optional) is a a full URL
 * @param {string} arg.path (required unless url) is the request path (defaults to root path)
 * @param {object} arg.payload (optional) is the payload to be sent along with the request. The payload has to satisfy the [json:api]{@link https://jsonapi.org} specification
 *
 * @return {promise} which follows the success/fails semantics of [axios]{@link https://github.com/axios/axios}. However, replaces the key `data -> payload`. Data are received from the jQuery [jqXHR]{@link https://api.jquery.com/Types/#jqXHR} object.
 */
export function apiCall({ method = 'GET', url = null, path = '', payload, timeout = 30000 } = {}) {
  if (!['GET', 'POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
    throw new TypeError(`Method ${method} not supported.`)
  }

  if (!API_URL) {
    API_URL = getApiUrl()
  }

  if (!url) {
    url = API_URL + path
  }

  if (payload && method !== 'GET') {
    payload = JSON.stringify(payload)
  }

  return new Promise((resolve, reject) => {
    $.ajax(url, {
      headers: {
        Accept: 'application/vnd.api+json'
      },
      contentType: 'application/vnd.api+json',
      dataType: 'json',
      method,
      data: payload,
      timeout
    })
      .done(function (data, textStatus, jqXHR) {
        resolve({
          status: jqXHR.status,
          statusText: textStatus,
          payload: jqXHR.responseJSON
        })
      })
      .fail(function (jqXHR, textStatus) {
        const err = new RequestError({
          status: jqXHR.status,
          statusText: textStatus,
          payload: jqXHR.responseJSON
        })
        reject(err)

        if (err.status === 401 && err.code === 'NOT_AUTHENTICATED') {
          console.warn(err)
          console.warn('Reload and threreby trigger redirecting to login...')
          window.location.reload()
        }
      })
  })
}

/**
 * Generic helper to build RESTful pathes.
 *
 * @function
 *
 * @param {object} arg
 * @param {string} arg.path has to be the path with optional placeholders for the parameters, e.g. `/api/users/:id`.
 * @param {object} arg.params are optional path paramters to be replaced
 *
 * @return {string} the path
 */
export function buildPath({ path = '', params = {} } = {}) {
  path = path.replace(/:\w+/g, (m) => {
    const p = params[m.slice(1)]

    if (!p) {
      throw new TypeError(`Path param ${m} is required by the path, but not provided in params.`)
    }

    return p
  })

  if (!path) {
    return path
  }

  // add leading `/`
  if (!/^\/\w+.*/.test(path)) {
    path = '/' + path
  }

  // rm trailing `/`
  if (/.*\/$/.test(path)) {
    path = path.slice(0, path.length - 1)
  }

  return path
}

export class RequestError extends Error {
  constructor({ status, statusText, payload }) {
    super(`Request failed with status code ${status}.`)
    this.payload = payload
    this.status = status
    this.statusText = statusText
    this.id = null
    this.code = 'UNKNOWN'
    if (statusText === 'timeout') {
      this.title = 'Request aborted. Timeout.'
      this.detail = 'We are sorry, server took too long to respond. Please try again.'
    } else {
      this.title = 'Server Error'
      this.detail = 'We are sorry, something went wrong.'
    }

    this.meta = null

    if (payload && payload.errors && payload.errors.length) {
      const err = payload.errors[0]
      if (!err) {
        return
      }

      if (err.title) {
        this.title = err.title
      }
      if (err.detail) {
        this.detail = err.detail
      }
      if (err.id) {
        this.id = err.id
      }
      if (err.code) {
        this.code = err.code
      }
      if (err.meta) {
        this.meta = err.meta
      }
    }
  }
}
